import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import CommentsPanel from './CommentsPanel';

const EMOJIS = ['🔥', '😭', '💀', '👑', '✨', '❤️', '🎵', '🤯'];

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

let ytApiLoaded = false;
let ytApiLoading = false;
const ytReadyCallbacks = [];

function loadYouTubeAPI() {
  if (ytApiLoaded) return Promise.resolve();
  if (ytApiLoading) return new Promise(resolve => ytReadyCallbacks.push(resolve));
  ytApiLoading = true;
  return new Promise(resolve => {
    ytReadyCallbacks.push(resolve);
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => {
      ytApiLoaded = true;
      ytApiLoading = false;
      ytReadyCallbacks.forEach(cb => cb());
      ytReadyCallbacks.length = 0;
    };
  });
}

function YouTubePlayer({ videoId, startSeconds, endSeconds }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(startSeconds || 0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [looping, setLooping] = useState(false);
  const intervalRef = useRef(null);
  const loopingRef = useRef(false);

  useEffect(() => { loopingRef.current = looping; }, [looping]);

  useEffect(() => {
    loadYouTubeAPI().then(() => {
      if (!containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: { start: startSeconds || 0, autoplay: 0, controls: 0, disablekb: 1, fs: 0, modestbranding: 1, rel: 0, showinfo: 0, iv_load_policy: 3, playsinline: 1 },
        events: {
          onReady: (e) => {
            e.target.seekTo(startSeconds || 0, true);
            setReady(true);
            const check = setInterval(() => {
              const d = e.target.getDuration();
              if (d > 0) { setDuration(d); clearInterval(check); }
            }, 300);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              const d = e.target.getDuration();
              if (d > 0) setDuration(d);
              intervalRef.current = setInterval(() => {
                if (playerRef.current?.getCurrentTime) {
                  const t = playerRef.current.getCurrentTime();
                  setCurrentTime(t);
                  if (loopingRef.current && endSeconds && t >= endSeconds) {
                    playerRef.current.seekTo(startSeconds || 0, true);
                  }
                }
              }, 300);
            } else {
              setIsPlaying(false);
              clearInterval(intervalRef.current);
            }
          },
        },
      });
    });
    return () => {
      clearInterval(intervalRef.current);
      if (playerRef.current?.destroy) playerRef.current.destroy();
    };
  }, [videoId, startSeconds]);

  const togglePlay = () => {
    if (!playerRef.current || !ready) return;
    if (isPlaying) playerRef.current.pauseVideo();
    else {
      playerRef.current.seekTo(currentTime > (startSeconds || 0) ? currentTime : (startSeconds || 0), true);
      playerRef.current.playVideo();
    }
  };

  const handleSeek = (e) => {
    const t = Number(e.target.value);
    setCurrentTime(t);
    if (playerRef.current?.seekTo) playerRef.current.seekTo(t, true);
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const startPercent   = duration > 0 ? ((startSeconds || 0) / duration) * 100 : 0;
  const endPercent     = duration > 0 && endSeconds ? (endSeconds / duration) * 100 : null;

  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 12, marginTop: 10, border: '0.5px solid rgba(212,83,126,0.15)' }}>
      <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: 320, height: 180, pointerEvents: 'none' }}>
        <div ref={containerRef} style={{ width: 320, height: 180 }} />
      </div>

      {!ready && (
        <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 11, color: 'rgba(245,230,237,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Loading player...
        </div>
      )}

      {ready && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={togglePlay} style={{
            width: 38, height: 38, borderRadius: '50%',
            background: 'linear-gradient(135deg, #D4537E, #993556)',
            color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {isPlaying ? '⏸' : '▶'}
          </button>

          <div style={{ flex: 1 }}>
            <div style={{ position: 'relative', height: 24, display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '100%', height: 3, background: 'rgba(245,230,237,0.1)', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                {endPercent !== null && (
                  <div style={{
                    position: 'absolute', left: `${startPercent}%`, width: `${endPercent - startPercent}%`,
                    height: '100%', background: 'rgba(212,83,126,0.5)',
                    borderRadius: 2, zIndex: 1,
                  }} />
                )}
                <div style={{ width: `${progressPercent}%`, height: '100%', background: '#D4537E', borderRadius: 2, transition: 'width 0.3s', position: 'relative', zIndex: 2 }} />
              </div>
              <input type="range" min="0" max={duration || 300} value={currentTime} onChange={handleSeek}
                style={{ position: 'absolute', width: '100%', opacity: 0, cursor: 'pointer', height: 24 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgba(245,230,237,0.35)', marginTop: 2, letterSpacing: '0.05em' }}>
              <span>{formatTime(currentTime * 1000)}</span>
              <span>{duration > 0 ? formatTime(duration * 1000) : '--:--'}</span>
            </div>
          </div>

          {endSeconds && (
            <button onClick={() => setLooping(v => !v)} title={looping ? 'Loop ON' : 'Loop section'} style={{
              flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
              background: looping ? 'rgba(212,83,126,0.2)' : 'transparent',
              border: looping ? '0.5px solid rgba(212,83,126,0.6)' : '0.5px solid rgba(255,255,255,0.1)',
              color: looping ? '#D4537E' : 'rgba(245,230,237,0.35)',
              fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}>🔁</button>
          )}

          <a href={`https://youtube.com/watch?v=${videoId}&t=${Math.floor(currentTime)}`}
            target="_blank" rel="noreferrer"
            style={{ fontSize: 10, color: 'rgba(212,83,126,0.7)', textDecoration: 'none', flexShrink: 0, letterSpacing: '0.05em' }}>
            YT ↗
          </a>
        </div>
      )}
    </div>
  );
}

export default function MomentCard({ moment, onTagClick }) {
  const { user, socket } = useApp();
  const [reactions, setReactions]     = useState(moment.reactions || []);
  const [commentCount, setCommentCount] = useState(moment.comment_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [showEmbed, setShowEmbed]     = useState(false);
  const [showShare, setShowShare]     = useState(false);
  const [copied, setCopied]           = useState(false);
  const [showSave, setShowSave]       = useState(false);
  const [collections, setCollections] = useState([]);
  const [saving, setSaving]           = useState(null);
  const [saved, setSaved]             = useState(null);

  // ── Add to Room ──
  const [addedToRoom, setAddedToRoom] = useState(false);

  const getActiveRoomId = () => {
    // Check if user is in a room by looking at the current URL or localStorage
    const path = window.location.pathname;
    if (path.startsWith('/room/')) return path.split('/room/')[1];
    return localStorage.getItem('active_room_id') || null;
  };

  const handleAddToRoom = () => {
    if (!user || !socket) return;
    const roomId = getActiveRoomId();
    if (!roomId) {
      alert('You need to be in a room first! Create or join a room, then add moments.');
      return;
    }
    const username = user.username || user.user_metadata?.username || user.email;
    socket.emit('room_queue_add', {
      roomId,
      username,
      moment: {
        id: moment.id,
        track_name: moment.track_name,
        artist_name: moment.artist_name,
        album_image: moment.album_image,
        timestamp_ms: moment.timestamp_ms,
        end_timestamp_ms: moment.end_timestamp_ms,
        spotify_track_id: moment.spotify_track_id,
        embed_url: moment.embed_url,
      },
    });
    setAddedToRoom(true);
    setTimeout(() => setAddedToRoom(false), 2000);
  };

  const getCount = (emoji) => {
    const r = reactions.find(r => r.emoji === emoji);
    return r ? parseInt(r.count) : 0;
  };

  const handleReact = async (emoji) => {
    if (!user) return;
    try {
      const res = await axios.post('https://songmoment.onrender.com/api/reactions', {
        moment_id: moment.id, user_id: user.id, emoji,
      });
      setReactions(res.data.reactions);
    } catch (err) { console.error(err); }
  };

  const getYoutubeId = () => {
    if (moment.spotify_track_id?.startsWith('youtube_')) return moment.spotify_track_id.replace('youtube_', '');
    if (moment.embed_url?.includes('youtube')) return moment.embed_url.split('/embed/')[1]?.split('?')[0];
    return null;
  };

  const youtubeId    = getYoutubeId();
  const startSeconds = Math.floor((moment.timestamp_ms || 0) / 1000);
  const endSeconds   = moment.end_timestamp_ms ? Math.floor(moment.end_timestamp_ms / 1000) : null;

  const shareUrl  = `https://songmoment.onrender.com/moment/${moment.id}`;
  const shareText = `🎵 Check out this moment from "${moment.track_name}" by ${moment.artist_name} at ${formatTime(moment.timestamp_ms)}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp  = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
  const shareInstagram = () => { navigator.clipboard.writeText(shareText + ' ' + shareUrl); alert('Link copied! Paste it in your Instagram story or bio.'); };

  const fetchCollections = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`https://songmoment.onrender.com/api/collections/user/${user.id}`);
      setCollections(res.data);
      setShowSave(true);
    } catch (err) { console.error(err); }
  };

  const saveToCollection = async (collectionId) => {
    setSaving(collectionId);
    try {
      await axios.post(`https://songmoment.onrender.com/api/collections/${collectionId}/moments`, { moment_id: moment.id });
      setSaved(collectionId);
      setTimeout(() => { setSaved(null); setShowSave(false); }, 1500);
    } catch (err) { console.error(err); }
    finally { setSaving(null); }
  };

  const actionBtnStyle = {
    fontSize: 10, letterSpacing: '0.08em', textTransform: 'none',
    color: 'rgba(245,230,237,0.45)',
    background: 'rgba(255,255,255,0.04)',
    border: '0.5px solid rgba(255,255,255,0.08)',
    borderRadius: 20, padding: '5px 12px', cursor: 'pointer',
    transition: 'border-color 0.15s, color 0.15s',
  };

  return (
    <div className="fade-in" style={{
      background: 'rgba(255,255,255,0.03)',
      border: '0.5px solid rgba(212,83,126,0.15)',
      borderRadius: 16, marginBottom: 16, overflow: 'hidden',
      transition: 'border-color 0.2s',
    }}
      onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.35)'}
      onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.15)'}
    >
      {/* Top: user + actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: `linear-gradient(135deg, #993556, #D4537E)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 500, color: '#fff',
          }}>
            {moment.username?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <div
              onClick={() => window.location.href = `/profile/${moment.username}`}
              style={{
                fontSize: 13, fontWeight: 600,
                cursor: 'pointer', color: '#ED93B1',
                textDecoration: 'none',
                transition: 'opacity 0.2s',
              }}
              onMouseOver={e => e.target.style.opacity = '0.7'}
              onMouseOut={e => e.target.style.opacity = '1'}
            >@{moment.username}</div>
            <div style={{ fontSize: 10, color: 'rgba(245,230,237,0.35)', marginTop: 1, letterSpacing: '0.05em' }}>{timeAgo(moment.created_at)}</div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '55%' }}>

          {/* ── Add to Room button ── */}
          {user && (
            <button
              onClick={handleAddToRoom}
              style={{
                ...actionBtnStyle,
                borderColor: addedToRoom ? 'rgba(34,197,94,0.5)' : 'rgba(147,83,212,0.3)',
                color: addedToRoom ? '#22c55e' : 'rgba(167,139,250,0.8)',
                background: addedToRoom ? 'rgba(34,197,94,0.08)' : 'rgba(147,83,212,0.08)',
              }}
              onMouseOver={e => {
                if (!addedToRoom) {
                  e.currentTarget.style.borderColor = 'rgba(147,83,212,0.6)';
                  e.currentTarget.style.color = '#a78bfa';
                }
              }}
              onMouseOut={e => {
                if (!addedToRoom) {
                  e.currentTarget.style.borderColor = 'rgba(147,83,212,0.3)';
                  e.currentTarget.style.color = 'rgba(167,139,250,0.8)';
                }
              }}
            >
              {addedToRoom ? '✓ Added!' : '🎧 Add to Room'}
            </button>
          )}

          <button
            onClick={() => setShowShare(v => !v)}
            style={actionBtnStyle}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,0.4)'; e.currentTarget.style.color = '#D4537E'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(245,230,237,0.45)'; }}
          >↗ Share</button>
          {user && (
            <button
              onClick={fetchCollections}
              style={actionBtnStyle}
              onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,0.4)'; e.currentTarget.style.color = '#D4537E'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(245,230,237,0.45)'; }}
            >+ Save</button>
          )}
        </div>
      </div>

      {/* Album art banner */}
      {moment.album_image ? (
        <div style={{ position: 'relative', margin: '12px 0 0', width: '100%', height: 200, overflow: 'hidden' }}>
          <img
            src={moment.album_image} alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.75 }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(8,6,8,0.95) 100%)' }} />
          <button onClick={() => setShowEmbed(v => !v)} style={{
            position: 'absolute', bottom: 12, right: 14,
            background: showEmbed ? 'rgba(8,6,8,0.8)' : 'rgba(212,83,126,0.9)',
            color: '#fff', border: showEmbed ? '0.5px solid rgba(212,83,126,0.5)' : 'none',
            borderRadius: 20, padding: '5px 13px',
            fontSize: 11, fontWeight: 500, letterSpacing: '0.05em',
            cursor: 'pointer', backdropFilter: 'blur(8px)',
            transition: 'background 0.2s',
          }}>
            {showEmbed ? '⏹' : '▶'} {formatTime(moment.timestamp_ms)}
            {moment.end_timestamp_ms ? ` → ${formatTime(moment.end_timestamp_ms)}` : ''}
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative', margin: '12px 0 0', width: '100%', height: 100, background: 'linear-gradient(135deg, #1a0a14, #2d1020, #1a0a14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 32, opacity: 0.2 }}>♪</span>
          <button onClick={() => setShowEmbed(v => !v)} style={{
            position: 'absolute', bottom: 10, right: 12,
            background: showEmbed ? 'rgba(8,6,8,0.8)' : 'rgba(212,83,126,0.9)',
            color: '#fff', border: showEmbed ? '0.5px solid rgba(212,83,126,0.5)' : 'none',
            borderRadius: 20, padding: '5px 13px',
            fontSize: 11, fontWeight: 500, letterSpacing: '0.05em',
            cursor: 'pointer', backdropFilter: 'blur(8px)',
          }}>
            {showEmbed ? '⏹' : '▶'} {formatTime(moment.timestamp_ms)}
            {moment.end_timestamp_ms ? ` → ${formatTime(moment.end_timestamp_ms)}` : ''}
          </button>
        </div>
      )}

      {/* Song info */}
      <div style={{ padding: '14px 16px 0' }}>
        <div style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 400, fontSize: 20,
          letterSpacing: '0.02em', color: '#f5e6ed', marginBottom: 3,
        }}>
          {moment.track_name}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(245,230,237,0.45)', letterSpacing: '0.04em' }}>
          {moment.artist_name}
        </div>
      </div>

      {/* Tags */}
      {moment.tags?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 16px 0' }}>
          {moment.tags.map(tag => (
            <span key={tag} onClick={() => onTagClick && onTagClick(tag)} style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 20,
              background: 'rgba(212,83,126,0.1)',
              border: '1px solid rgba(212,83,126,0.3)',
              color: 'var(--accent2)',
              cursor: onTagClick ? 'pointer' : 'default',
              letterSpacing: '0.03em',
              transition: 'all 0.15s',
            }}
              onMouseOver={e => { if (onTagClick) e.target.style.background = 'rgba(212,83,126,0.2)'; }}
              onMouseOut={e => { if (onTagClick) e.target.style.background = 'rgba(212,83,126,0.1)'; }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Note */}
      {moment.note && (
        <div style={{ margin: '12px 16px 0', padding: '10px 12px', background: 'rgba(212,83,126,0.06)', borderRadius: 8, fontSize: 13, color: 'rgba(245,230,237,0.65)', borderLeft: '2px solid rgba(212,83,126,0.5)', fontStyle: 'italic' }}>
          "{moment.note}"
        </div>
      )}

      {/* YouTube Player */}
      {showEmbed && (
        <div style={{ padding: '0 16px' }}>
          {youtubeId ? (
            <YouTubePlayer videoId={youtubeId} startSeconds={startSeconds} endSeconds={endSeconds} />
          ) : moment.preview_url ? (
            <div style={{ marginTop: 10 }}>
              <audio controls style={{ width: '100%', borderRadius: 8 }}>
                <source src={moment.preview_url} type="audio/mpeg" />
              </audio>
              <p style={{ fontSize: 10, color: 'rgba(245,230,237,0.35)', marginTop: 4, letterSpacing: '0.05em' }}>30 second preview</p>
            </div>
          ) : (
            <p style={{ fontSize: 11, color: 'rgba(245,230,237,0.35)', marginTop: 8, letterSpacing: '0.06em' }}>No preview available</p>
          )}
        </div>
      )}

      {/* Share panel */}
      {showShare && (
        <div className="slide-in" style={{ margin: '12px 16px 0', padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '0.5px solid rgba(212,83,126,0.2)' }}>
          <p style={{ fontSize: 10, color: 'rgba(245,230,237,0.35)', marginBottom: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Share this moment</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={copyLink} style={{
              fontSize: 11, padding: '6px 14px',
              background: copied ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.04)',
              border: `0.5px solid ${copied ? 'rgba(74,222,128,0.5)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 20, color: copied ? '#4ade80' : 'rgba(245,230,237,0.65)',
              cursor: 'pointer', letterSpacing: '0.05em',
            }}>
              {copied ? '✓ Copied' : '🔗 Copy link'}
            </button>
            <button onClick={shareWhatsApp} style={{ fontSize: 11, padding: '6px 14px', background: 'rgba(37,211,102,0.15)', border: '0.5px solid rgba(37,211,102,0.3)', borderRadius: 20, color: '#25D366', cursor: 'pointer', letterSpacing: '0.05em' }}>
              WhatsApp
            </button>
            <button onClick={shareInstagram} style={{ fontSize: 11, padding: '6px 14px', background: 'rgba(212,83,126,0.1)', border: '0.5px solid rgba(212,83,126,0.3)', borderRadius: 20, color: '#D4537E', cursor: 'pointer', letterSpacing: '0.05em' }}>
              Instagram
            </button>
          </div>
        </div>
      )}

      {/* Save to collection panel */}
      {showSave && user && (
        <div className="slide-in" style={{ margin: '12px 16px 0', padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '0.5px solid rgba(212,83,126,0.2)' }}>
          <p style={{ fontSize: 10, color: 'rgba(245,230,237,0.35)', marginBottom: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Save to collection</p>
          {collections.length === 0 ? (
            <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.35)', letterSpacing: '0.04em' }}>No collections yet — create one first!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {collections.map(c => (
                <button key={c.id} onClick={() => saveToCollection(c.id)} disabled={saving === c.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', borderRadius: 10,
                  background: saved === c.id ? 'rgba(74,222,128,0.08)' : 'rgba(255,255,255,0.03)',
                  border: `0.5px solid ${saved === c.id ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.08)'}`,
                  color: saved === c.id ? '#4ade80' : 'rgba(245,230,237,0.65)',
                  fontSize: 13, cursor: 'pointer', textAlign: 'left',
                }}>
                  <span>{c.name}</span>
                  <span style={{ fontSize: 10, letterSpacing: '0.05em', color: saved === c.id ? '#4ade80' : 'rgba(245,230,237,0.35)' }}>
                    {saved === c.id ? '✓ Saved' : saving === c.id ? '...' : `${c.moment_count} moments`}
                  </span>
                </button>
              ))}
            </div>
          )}
          <button onClick={() => setShowSave(false)} style={{ marginTop: 10, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(245,230,237,0.3)', background: 'none', border: 'none', cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      )}

      {/* Divider */}
      <div style={{ height: '0.5px', background: 'linear-gradient(90deg, transparent, rgba(212,83,126,0.25), transparent)', margin: '12px 16px 0' }} />

      {/* Reactions + Comments */}
      <div style={{ padding: '10px 16px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {EMOJIS.map(emoji => {
            const count = getCount(emoji);
            return (
              <button key={emoji} onClick={() => handleReact(emoji)} style={{
                fontSize: 15, padding: '4px 6px',
                background: count > 0 ? 'rgba(212,83,126,0.1)' : 'transparent',
                border: count > 0 ? '0.5px solid rgba(212,83,126,0.3)' : 'none',
                borderRadius: 8,
                opacity: count > 0 ? 1 : 0.55,
                cursor: user ? 'pointer' : 'default',
                transition: 'opacity 0.15s',
              }}>
                {emoji}{count > 0 && <span style={{ marginLeft: 3, fontSize: 10, color: '#D4537E' }}>{count}</span>}
              </button>
            );
          })}
        </div>

        <button onClick={() => setShowComments(v => !v)} style={{
          fontSize: 11, letterSpacing: '0.06em',
          color: 'rgba(245,230,237,0.45)',
          background: 'rgba(255,255,255,0.04)',
          border: '0.5px solid rgba(255,255,255,0.08)',
          borderRadius: 20, padding: '5px 14px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          💬 {commentCount > 0 ? `${commentCount} comment${commentCount > 1 ? 's' : ''}` : 'Comment'}
        </button>
      </div>

      {showComments && (
        <div style={{ padding: '0 16px 14px' }}>
          <CommentsPanel
            momentId={moment.id}
            onCountChange={(delta) => setCommentCount(c => c + delta)}
          />
        </div>
      )}
    </div>
  );
}